import { type ChangeEvent, type MouseEvent, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "@/app/components/ui/button";
import { Checkbox } from "@/app/components/ui/checkbox";
import { Label } from "@/app/components/ui/label";
import type { ResumeContextValue } from "@/app/types/resume";
import tips from "../../../../assets/copy/tips";
import AutoSuggestion from "../../../../components/AutoSuggestion";
import SkipSectionDialog from "../../../../components/ResumeBuilder/SkipSectionDialog";
import Tips from "../../../../components/Tips";
import InputField from "../../../../components/UIComponents/InputField";
import SelectField from "../../../../components/UIComponents/SelectField";
import ResumeContext from "../../../../context/ResumeContext";
import { NavigationHandler } from "../../../../handlers/navigationHandler";
import { nextAfterWorkHistory, useEducationFirst } from "../../../../router/sectionOrder";
import { workExperienceSchema } from "../../../../schemas/resume";
import options from "../../../../seed/dropDownOptions";
import mapping from "../../../../seed/metadata/filemapping";
import ResumeWorkDetails, { type WorkHistoryFormObject } from "../ResumeWorkDetails/ResumeWorkDetails";

interface WorkHistoryFormState {
	referenceId: string;
	jobTitle: string;
	employer: string;
	city?: string;
	region?: string;
	country?: string;
	startDate: string;
	endDate: string;
	currentEmployer: boolean | "";
	relevantSkills: string;
}

interface WorkHistoryErrors {
	jobTitle?: string;
	employer?: string;
	address?: string;
	city?: string;
	country?: string;
	startDate?: string;
	endDate?: string;
}

const ResumeWorkHistory = () => {
	const navigate = useNavigate();

	const navigationHandler = new NavigationHandler();
	const link = useLocation().pathname.split("/")[2];

	const locationState = useLocation().state as { referenceId?: string } | null;
	const referenceId = locationState && locationState.referenceId;
	const locations: string[] = mapping["districtsRegions"][0].districtRegions;
	const [country, setCountry] = useState("Tanzania");

	const { resumeData } = useContext(ResumeContext) as ResumeContextValue;

	const { journal } = resumeData;
	const resumeCurrentStatus = journal ? journal.currentStatus : false;
	const isComplete = resumeCurrentStatus == "COMPLETED";

	//Declaring the form states
	const [workHistory, setWorkHistory] = useState<WorkHistoryFormState>({
		referenceId: "",
		jobTitle: "",
		employer: "",
		startDate: "",
		endDate: "",
		currentEmployer: "",
		relevantSkills: "",
	});

	const [isInputCompleted, setIsInputCompleted] = useState(false);
	const [workLocation, setWorkLocation] = useState("");
	const [showSkipDialog, setShowSkipDialog] = useState(false);

	//The form event handler
	const onChangeInput = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		if (e.target.name === "workLocation") {
			setWorkLocation(e.target.value);
		} else if (e.target.name === "country") {
			setCountry(e.target.value);
		} else {
			setWorkHistory((prevState) => {
				const capitalized = e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1);
				return {
					...prevState,
					[e.target.name]: capitalized,
				};
			});
		}
	};

	const [errors, setErrors] = useState<WorkHistoryErrors>({
		jobTitle: "",
		employer: "",
		address: "",
		startDate: "",
		endDate: "",
	});

	const [modalData, setModalData] = useState<WorkHistoryFormObject | null>(null);
	const [showDetailsModal, setShowDetailsModal] = useState(false);
	//form submission handler
	const addWorkHistory = (e: MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();

		let dataToValidate: WorkHistoryFormState & { address: string } = { ...workHistory, address: workLocation };

		dataToValidate = {
			...workHistory,
			address: `${dataToValidate.address}, ${country}`,
		};

		// Form validation
		const validation = workExperienceSchema.safeParse({ ...dataToValidate, country });
		if (!validation.success) {
			const fieldErrors: WorkHistoryErrors = {};
			for (const issue of validation.error.issues) {
				const field = issue.path[0] as keyof WorkHistoryErrors;
				fieldErrors[field] ??= issue.message;
			}
			return setErrors(fieldErrors);
		}
		setErrors({});

		const address = dataToValidate.address.split(",");

		const workHistoryObject: WorkHistoryFormObject = {
			...workHistory,
			details: "",
			city: address[0],
			region: address[1],
			country: address[2],
		};

		//* This is just to that I can move on to the next page
		if (isInputCompleted) {
			setModalData(workHistoryObject);
			setShowDetailsModal(true);
		}
	};

	useEffect(() => {
		const address = `${workLocation}, ${country}`;
		setIsInputCompleted(workExperienceSchema.safeParse({ ...workHistory, address, country }).success);
	}, [workHistory, workLocation, country]);

	useEffect(() => {
		if (resumeData.workHistory.length > 0 && resumeData.workHistory[0].jobTitle !== "" && referenceId) {
			const data = resumeData.workHistory.filter((item) => item.referenceId === referenceId)[0];
			if (!data) return;

			// Compute + set both pieces of state directly — never call one setter
			// from inside another's updater (updaters must be pure and can replay
			// under StrictMode / concurrent rendering).
			setWorkLocation(`${data.city},${data.region},${data.country}`);
			setWorkHistory({
				referenceId: data.referenceId && data.referenceId,
				jobTitle: data.jobTitle,
				employer: data.employer,
				city: data.city,
				region: data.region,
				country: data.country,
				startDate: data.startDate,
				endDate: data.endDate,
				currentEmployer: data.currentEmployer,
				relevantSkills: data.relevantSkills,
			});
		}
	}, [resumeData, referenceId]);

	// Skills for a reader with no experience, who was asked for education before this
	// screen; Education for everyone else, who has not reached it yet.
	const skipDestination = nextAfterWorkHistory(useEducationFirst(), {
		educationCount: resumeData.education.length,
		workHistoryCount: resumeData.workHistory.length,
	});

	const skipSection = () => {
		navigationHandler.getNext(navigationHandler.template.navigationFlow, link, isComplete);
		navigate(`/resume/${skipDestination}`);
	};

	return (
		<>
			<SkipSectionDialog open={showSkipDialog} onOpenChange={setShowSkipDialog} onSkip={skipSection} />

			<div className="min-h-screen">
				<div className="mb-10 md:mb-12">
					<h2 className="mb-3 !font-bold !text-2xl text-gray-900 !md:text-3xl">Tell us about your most recent job </h2>
					{/* Held to the same width as the form below, so the Tips link lands on the
					    fields' right edge rather than way out at the page edge. */}
					<div className="flex w-full items-start justify-between gap-3 md:w-1/2">
						<p className="text-gray-600">
							We&#39;ll start there and work backward. <i>No jobs yet? Use Skip for now.</i>
						</p>
						<Tips content={tips.workHistory} />
					</div>
				</div>
				<div className="w-full md:w-1/2">
					<form action="">
						<div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
							<div className="mb-4 md:col-span-2">
								<InputField
									name="jobTitle"
									type="text"
									label="Job Title"
									placeholder="Enter Job Title"
									value={workHistory.jobTitle}
									onChange={onChangeInput}
									error={errors.jobTitle}
								/>
							</div>
							<div className="mb-4 md:col-span-2">
								<InputField
									type="text"
									label="Employer"
									name="employer"
									value={workHistory.employer}
									onChange={onChangeInput}
									error={errors.employer}
									placeholder="Enter Employer Name"
								/>
							</div>

							<div className="mb-4">
								<SelectField
									name="country"
									value={country}
									onChange={onChangeInput}
									options={options.phoneCountryCodeOptions.map(({ name }: { name: string }) => ({
										value: name,
										name,
									}))}
									label="Country"
								/>
							</div>

							<div className="mb-4">
								{!(country === "Tanzania") && (
									<InputField
										type="text"
										label="Ex: City, Region"
										name="workLocation"
										value={workLocation}
										onChange={onChangeInput}
										placeholder="Enter Work Location"
										error={errors.address}
									/>
								)}
								{country === "Tanzania" && (
									<AutoSuggestion
										suggestions={locations}
										label="Eg: District, Region, Country"
										type="text"
										name="workLocation"
										userInput={workLocation}
										setUserInput={setWorkLocation}
										isForm={true}
										error={errors.address}
									/>
								)}
							</div>

							<div className="mb-4">
								<InputField
									type="date"
									label="Start Date"
									name="startDate"
									value={workHistory.startDate}
									onChange={onChangeInput}
									error={errors.startDate}
								/>
							</div>
							<div className="mb-4">
								{!workHistory.currentEmployer && (
									<InputField
										type="date"
										label="End Date"
										name="endDate"
										disabled={!!workHistory.currentEmployer}
										value={workHistory.endDate}
										onChange={onChangeInput}
										error={errors.endDate}
									/>
								)}
							</div>
							{/* A plain div, not a label: `Label` renders its own <label>, and nesting
							    one inside another is invalid markup that swallowed clicks on the
							    caption. `htmlFor` keeps the caption clickable. */}
							<div className="mb-3 flex items-center gap-3 md:col-span-2">
								<Checkbox
									id="customCheck"
									name="currentEmployer"
									checked={!!workHistory.currentEmployer}
									onCheckedChange={() => {
										setWorkHistory((prevState) => {
											return {
												...prevState,
												endDate: "",
												currentEmployer: !prevState.currentEmployer,
											};
										});
									}}
								/>
								<Label className="cursor-pointer" htmlFor="customCheck">
									I still work here
								</Label>
							</div>
						</div>
						<div className="mt-6 flex items-center justify-end gap-3">
							{/* Forward to Skills, the section after this one — matching `skipSection`
							    above. This used to go to education-information, which was the next
							    section only while work history came first; it now walks backwards. */}
							<Button variant="ghost" type="button" onClick={skipSection}>
								Skip for now
							</Button>
							<Button
								type="button"
								onClick={(e) => {
									if (!isInputCompleted) {
										setShowSkipDialog(true);
									}
									addWorkHistory(e);
								}}
							>
								Continue
							</Button>
						</div>
					</form>
				</div>
			</div>

			{/* Details Modal */}
			{showDetailsModal && modalData && (
				<ResumeWorkDetails closeModal={() => setShowDetailsModal(false)} workHistoryObject={modalData} />
			)}
		</>
	);
};

export default ResumeWorkHistory;
